import { ReactElement, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Skeleton from '../../components/Skeleton';
import SectionHeader from '../../components/SectionHeader';
import { useSessions } from '../../hooks/useSessions';
import { useRidersById } from '../../hooks/useRiders';
import {
  SessionAnalysisTypeUtils,
} from '../../types/SessionAnalysisType';
import { SessionIcon } from '../../utils/sessionIconUtils';
import SessionColorIndicator
  from '../../components/SessionColorIndicator';
import { compareSessionsDesc } from '../../utils/sessionSort';

interface PickerState {
  returnTo?: string;
  sessionId?: string;
  mode?: 'gates' | 'route';
}

export default function SessionPicker(): ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = location.state as PickerState | null;
  const returnTo = routeState?.returnTo;
  const currentSessionId = routeState?.sessionId;
  const mode = routeState?.mode ?? 'route';

  const {
    isPending,
    isFetching,
    data: sessions,
  } = useSessions();
  const { data: ridersById } = useRidersById();

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--page-bg-color', '#f3f4f6',
    );
    return () => {
      document.documentElement.style.setProperty(
        '--page-bg-color', 'transparent',
      );
    };
  }, []);

  const filtered = useMemo(() => {
    const arr = Array.isArray(sessions) ? sessions : [];
    return arr
      .filter((s: any) => {
        if (s.id === currentSessionId) return false;
        if (s.hidden) return false;
        if (SessionAnalysisTypeUtils.isSingletonLevel(
          s.analysis_type,
        )) return false;
        if (mode === 'gates') return !!s.gates_id;
        return !!s.route_id;
      })
      .sort((a: any, b: any) => compareSessionsDesc(a, b, ridersById));
  }, [sessions, currentSessionId, mode, ridersById]);

  const handleSelect = (session: any) => {
    const cb = (window as any).__sessionPickerCallback;
    if (cb) {
      cb({
        gates_id: session.gates_id ?? null,
        route_id: session.route_id ?? null,
      });
    }
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigate(-1);
    }
  };

  const entry = (label: string, value?: string) => (
    <div className="cr-text-base">
      <div
        className={
          'cr-inline cr-break-after-auto'
          + ' cr-font-bold cr-uppercase'
        }
      >
        {label}:&nbsp;
      </div>
      <div
        className="cr-inline cr-font-normal"
        style={{ fontFamily: '\'Poppins\', sans-serif' }}
      >
        {value}
      </div>
    </div>
  );

  const backLink = returnTo
    ? { to: returnTo, text: 'Back to Session' }
    : undefined;

  const titleLabel = mode === 'gates'
    ? 'Select Session (Gates)'
    : 'Select Session (Route)';

  const grid = filtered.map((session: any) => (
    <button
      type="button"
      key={session.id}
      onClick={() => handleSelect(session)}
      className={
        'cr-relative cr-flex cr-flex-col cr-gap-4'
        + ' cr-rounded-2xl cr-bg-white cr-p-4'
        + ' cr-text-left cr-text-cirrusBlue-400'
        + ' cr-shadow-md cr-cursor-pointer'
        + ' hover:cr-ring-2 hover:cr-ring-cirrusBlue-300'
        + ' md:cr-pb-5 lg:cr-pb-20'
      }
    >
      <div className="cr-flex cr-items-center cr-gap-3">
        <SessionIcon
          analysisType={session.analysis_type}
          size={32}
          className="cr-text-cirrusBlue-400"
        />
        <div
          className={
            'font-montserrat cr-text-2xl'
            + ' cr-font-semibold'
          }
        >
          {session.date}
        </div>
      </div>
      {entry(
        'Rider',
        ridersById?.get(session.rider_id)?.nickname,
      )}
      {entry('Description', session.description)}
      <SessionColorIndicator sessionId={session.id} />
    </button>
  ));

  return (
    <>
      <SectionHeader
        title={titleLabel}
        isLoading={isFetching}
        backLink={backLink}
        titleClassName={
          '!cr-text-3xl !cr-font-extrabold'
          + ' cr-bg-gradient-to-r cr-from-blue-900'
          + ' cr-to-blue-600 cr-bg-clip-text'
          + ' !cr-text-transparent'
        }
      />
      <Skeleton isPending={isPending}>
        {filtered.length === 0 ? (
          <div className="cr-text-center cr-text-gray-500">
            No sessions with{' '}
            {mode === 'gates' ? 'gates' : 'routes'}{' '}
            found.
          </div>
        ) : (
          <div
            className={
              'cr-flex cr-flex-col cr-gap-6'
              + ' md:cr-grid md:cr-grid-cols-4'
            }
          >
            {grid}
          </div>
        )}
      </Skeleton>
    </>
  );
}
